import {
  Award,
  BookOpen,
  Gamepad2,
  type LucideIcon,
  Mic,
  Music,
  Sparkles,
  Sprout,
  Utensils,
} from "lucide-react";
import type { AgendaConfig, Event } from "@/lib/event-schema";
import { Ornament } from "@/components/ui/Ornament";
import { cn } from "@/lib/cn";

const iconMap: Record<NonNullable<AgendaConfig["items"][number]["icon"]>, LucideIcon> = {
  prayer: BookOpen,
  speech: Mic,
  meal: Utensils,
  poem: Sparkles,
  game: Gamepad2,
  award: Award,
  kids: Sprout,
  music: Music,
};

type Props = {
  config: AgendaConfig;
  event: Event;
};

export function AgendaModule({ config, event }: Props) {
  return (
    <section
      id="agenda"
      className="relative mx-auto w-full max-w-5xl px-5 py-16 sm:px-6 lg:px-8 lg:py-24"
      aria-labelledby={`${event.slug}-agenda`}
    >
      <header className="flex flex-col items-center text-center mb-12">
        <p className="eyebrow text-accent">Program</p>
        <h2
          id={`${event.slug}-agenda`}
          className="display-title mt-3 text-[clamp(2rem,4.4vw,3.25rem)] leading-tight"
        >
          {config.title ?? "The day, hour by hour"}
        </h2>
        {config.dateLabel ? (
          <p className="mt-2 text-sm text-muted">{config.dateLabel}</p>
        ) : null}
      </header>

      <ol className="relative">
        {/* Vertical rail */}
        <span
          aria-hidden
          className="absolute left-[28px] top-2 bottom-2 w-px bg-gradient-to-b from-accent/40 via-line to-line/40 md:left-1/2"
        />

        {config.items.map((item, i) => {
          const Icon = item.icon ? iconMap[item.icon] : Sparkles;
          const isRight = i % 2 === 1;
          return (
            <li
              key={i}
              className={cn(
                "relative grid grid-cols-[64px,1fr] gap-4 pb-10 last:pb-0 md:grid-cols-2 md:gap-8",
              )}
            >
              {/* Marker */}
              <div
                className={cn(
                  "row-start-1 row-end-2 flex items-start justify-center md:col-span-2 md:col-start-1 md:row-start-1 md:row-end-2",
                )}
                style={{ gridColumn: "1 / 2" }}
              >
                <div
                  className={cn(
                    "relative z-10 mt-1 grid h-14 w-14 place-items-center rounded-2xl border transition-all md:absolute md:left-1/2 md:-translate-x-1/2",
                    item.highlight
                      ? "border-accent/50 bg-accent text-accent-on shadow-glow"
                      : "border-line/70 bg-surface-raised text-ink/80",
                  )}
                >
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
              </div>

              {/* Content card — alternate sides on desktop */}
              <article
                className={cn(
                  "relative rounded-2xl border border-line/60 bg-surface-raised/70 p-5 backdrop-blur transition-all hover:border-accent/60 hover:shadow-card",
                  "md:col-span-1 md:p-6",
                  isRight ? "md:col-start-2" : "md:col-start-1 md:text-right",
                  item.highlight && "border-accent/40 bg-accent-soft/40",
                )}
              >
                <p
                  className={cn(
                    "font-mono text-xs tracking-wide text-accent",
                    isRight ? "md:text-left" : "md:text-right",
                  )}
                >
                  {item.start}
                  {item.end ? ` — ${item.end}` : null}
                </p>
                <h3 className="mt-2 font-display text-xl font-light leading-snug text-ink">
                  {item.title}
                </h3>
                {item.description ? (
                  <p className="mt-1.5 text-sm text-muted leading-relaxed">
                    {item.description}
                  </p>
                ) : null}
                {item.highlight ? (
                  <span
                    className={cn(
                      "mt-3 inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] text-accent-ink dark:text-accent",
                      isRight ? "md:ml-0" : "md:ml-auto",
                    )}
                  >
                    <span className="h-1 w-1 rounded-full bg-accent animate-shimmer" />
                    Parallel
                  </span>
                ) : null}
              </article>
            </li>
          );
        })}
      </ol>

      <Ornament className="mt-16" />
    </section>
  );
}
